import { useEffect, startTransition } from 'react'
import { channelsAPI } from '../services/api'
import { storage } from '../utils/storage'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChannelViewEffects')

/**
 * useChannelViewEffects - Side effects for CompactChannelsView
 * Consolidates 5 useEffect blocks for channel state management
 */
export function useChannelViewEffects({
  user,
  isAuthenticated,
  isAdmin,
  channels,
  activeChannel,
  messages,
  messagesEndRef,
  lastProcessedChannelRef,
  lastFetchedChannelRef,
  hasAutoSelectedTabRef,
  isSupportChannel,
  hasNoMessages,
  onUnreadCountChange,
  setMessages,
  setNewMessage,
  setShowContactForm,
  setActiveTab,
  setContactRateLimitTime,
  setChannels,
  fetchChannels,
  fetchMessages,
}) {
  // Main initialization — runs when user/auth changes
  useEffect(() => {
    setMessages([])
    setNewMessage('')
    setShowContactForm(false)
    fetchChannels()

    if (!hasAutoSelectedTabRef.current) {
      setActiveTab(isAuthenticated ? 'user' : 'admin')
    }

    if (isAuthenticated && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') logger.info('Desktop notifications enabled')
      })
    }

    if (!isAuthenticated) {
      const lastSubmission = storage.get('lastContactSubmission')
      if (lastSubmission) {
        const lastTime = parseInt(lastSubmission)
        const now = Date.now()
        const oneHour = 60 * 60 * 1000
        if (now - lastTime < oneHour) {
          setContactRateLimitTime(lastTime + oneHour)
        }
      }
    }
  }, [user?.id, isAdmin, isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset auto-select tracking when user changes
  useEffect(() => {
    hasAutoSelectedTabRef.current = false
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Background channel list polling (30s interval)
  useEffect(() => {
    if (!isAuthenticated || channels.length === 0) return
    const interval = setInterval(() => {
      startTransition(() => fetchChannels(true))
    }, 30000)
    return () => clearInterval(interval)
  }, [isAuthenticated, channels.length, fetchChannels])

  // Show/hide contact form based on channel type
  useEffect(() => {
    if (isSupportChannel && hasNoMessages && !isAdmin) {
      setShowContactForm(true)
    } else {
      setShowContactForm(false)
    }
  }, [activeChannel, messages, isSupportChannel, hasNoMessages, isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch messages when active channel changes
  useEffect(() => {
    if (!activeChannel || !isAuthenticated) return
    const channelId = activeChannel.id
    if (lastProcessedChannelRef.current === channelId) return
    lastProcessedChannelRef.current = channelId

    setChannels(prev => {
      const needsUpdate = prev.some(ch =>
        ch.id === channelId && ((ch.unreadCount || 0) !== 0 || (ch.needsReply || false) !== false)
      )
      if (!needsUpdate) return prev
      return prev.map(ch => ch.id === channelId ? { ...ch, unreadCount: 0, needsReply: false } : ch)
    })

    channelsAPI.markChannelAsRead(channelId).catch(err => {
      logger.error('Failed to mark channel as read', err, { channelId })
    })

    if (onUnreadCountChange) onUnreadCountChange()

    if (lastFetchedChannelRef.current !== channelId) {
      lastFetchedChannelRef.current = channelId
      fetchMessages(channelId, { replace: true })
    }
  }, [activeChannel?.id, isAuthenticated, fetchMessages, setChannels, onUnreadCountChange]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages]) // eslint-disable-line react-hooks/exhaustive-deps
}
